import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdDelete } from 'react-icons/md';
import MainButton from '../../../shared/layout/buttons/MainButton';

const PromotionList = ({ campaignRef, selectedPromotionIndex, onSelectPromotion, onAddPromotion, onDeletePromotion }) => {
    const { t } = useTranslation();
    const promotions = campaignRef.current?.promotions || [];

    return (
        <ListContainer>
            <Header>
                <h3>{t('Campaigns.PromotionList.promotionsList')}</h3>
                <MainButton size="small" variant="secondary" onClick={onAddPromotion} icon={<MdAdd />}>
                    {t('Campaigns.PromotionList.addPromotion')}
                </MainButton>
            </Header>
            <ListGroup>
                {promotions.map((promotion, index) => (
                    <ListItem
                        key={index}
                        $active={selectedPromotionIndex === index}
                        onClick={() => onSelectPromotion(index)}
                    >
                        <span className="promo-name">{promotion.name || t('Campaigns.PromotionList.promotion', { index: index + 1 })}</span>
                        {onDeletePromotion && (
                            <div className="actions">
                                <MainButton
                                    variant="danger"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeletePromotion(index);
                                    }}
                                    style={{ padding: '0.375rem 0.625rem', minWidth: 'auto' }}
                                    icon={<MdDelete size={18} />}
                                />
                            </div>
                        )}
                    </ListItem>
                ))}
            </ListGroup>
        </ListContainer>
    );
};

export default PromotionList;

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 700;
        color: #111827;
        letter-spacing: -0.01em;
    }
`;

const ListGroup = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ListItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.625rem 1rem; /* Matched padding to sidebar */
    margin: 0.25rem 0.5rem; /* Slight margin for the floater feel */
    cursor: pointer;
    font-size: 0.95rem;
    color: ${({ $active }) => ($active ? 'var(--color-primary)' : '#4b5563')};
    background-color: ${({ $active }) => ($active ? 'var(--color-primary-bg-soft)' : 'transparent')};
    font-weight: ${({ $active }) => ($active ? '600' : '450')};
    letter-spacing: -0.01em; /* Pro tracking */
    position: relative;
    border-radius: 10px; /* Consistent rounding */
    transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
    overflow: hidden;

    /* Pro Standard Floating Pill Accent */
    &::before {
        content: '';
        position: absolute;
        left: 6px; /* Floating inside the edge */
        top: 50%;
        width: 4px;
        height: 20px; /* Distinct pill height */
        background-color: var(--color-primary);
        border-radius: 4px; /* Fully rounded pill */
        opacity: ${({ $active }) => ($active ? 1 : 0)};
        transform: translateY(-50%) scaleY(${({ $active }) => ($active ? 1 : 0)});
        transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease;
    }

    /* Active Background Ghosting */
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgba(130, 22, 146, 0.05);
        opacity: ${({ $active }) => ($active ? 1 : 0)};
        transition: opacity 0.3s ease;
        z-index: -1;
    }

    .promo-name {
        flex: 1;
        transition: transform 0.25s ease;
    }

    .actions {
        opacity: 1;
        transform: translateX(0);
        transition: all 0.25s ease;
    }

    &:hover {
        background-color: rgba(130, 22, 146, 0.035);
        color: var(--color-primary);

        &::before {
            opacity: ${({ $active }) => ($active ? 1 : 0.4)};
            transform: scaleX(1);
        }

        .promo-name {
            transform: translateX(4px);
        }

        .actions {
            opacity: 1;
            transform: translateX(0);
        }
    }
`;
